<template>
  <div>

    <div class="box">

      <div class="flex flex-wrap items-center">
        <filtering-toggle-button :filtering="filtering" />

        <div class="flex-grow mb-1">
          <list-search v-model="searchInput" @search="getJobs"/>
          <p class="text-xs mt-0 ml-1" v-if="query.search !== ''">
            Searching for <span class="text-red-500 font-bold">{{ query.search }}</span>
          </p>
        </div>

        <div class="flex">
          <div
            v-if="hasPermission('job.create') && buttonNew"
            class="btn btn-purple text-sm mr-2 h-10 flex items-center px-3 mb-1"
            @click="goToCreateJob()"
          >
            <i class="fas fa-plus"></i>
            <span class="ml-2">New Job</span>
          </div>

          <button class="btn w-10 h-10 flex items-center justify-center focus:outline-none hover:text-green-500 mb-1" @click="getJobs">
            <i class="fas fa-sync-alt"></i>
          </button>
        </div>
      </div>

      <div class="flex flex-wrap">
        <div
          class="w-full lg:w-2/12 pr-2 pt-4 border-r border-theme-darker"
          v-show="filtering.active"
        >
          <job-filters
            :filters.sync="filters"
            @updated="getJobs"
            @has-changes="filtering.hasChanges = $event"
          />
        </div>

        <div class="w-full overflow-x-auto" :class="{ 'lg:w-10/12': filtering.active}">
          <p class="text-lg text-center my-12" v-if="!hasJobs">No data to display</p>
          <div v-else>
            <table class="table table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th v-if="showCustomerColumn">Customer</th>
                  <th>Job type</th>
                  <th>Make / Model</th>
                  <th>Plates</th>
                  <th>Created at</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(job, i) in displayJobs" :key="i">
                  <td class="font-bold">{{ job.id }}</td>
                  <td v-if="showCustomerColumn">
                    {{ '#' + job.contact.id + ' ' + job.contact.first_name + ' ' + job.contact.last_name }}
                    <span v-if="job.contact.company_name">({{ job.contact.company_name}})</span>
                  </td>
                  <td>{{ job.job_type.name }}</td>
                  <td>{{ job.vehicle.make + ' - ' +  job.vehicle.model}}</td>
                  <td>{{ job.vehicle.plate_numbers }}</td>
                  <td>{{ $moment(job.created_at).format(config.dateTimeFormats.display) }}</td>
                  <td>
                    <span class="font-bold border border-green-800 text-green-600 light:text-green-700 light:border-green-300 text-xs py-1 px-1 rounded" v-if="job.status === 'completed'">completed</span>
                    <span class="font-bold bg-orange-500 text-xs text-white py-1 px-3 rounded" v-if="job.status === 'ongoing'">ongoing</span>
                  </td>
                  <td>
                    <div class="flex">
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/job/details/' + job.id)"
                      ><i class="fas fa-info"></i></a>
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="$router.push('/job/edit/' + job.id)"
                        v-if="job.status === 'ongoing' && hasPermission('job.edit')"
                      ><i class="fas fa-edit"></i></a>
                      <a
                        class="hover:text-theme-darker text-base cursor-pointer p-2"
                        @click.stop="deleteJobs(job.id)"
                        v-if="job.status === 'ongoing' && hasPermission('job.delete')"
                      ><i class="fas fa-trash"></i></a>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="w-full flex justify-center">
          <pagination-component
            class="mt-8"
            v-if="hasJobs && pagination.totalRows / pagination.perPage > 1"
            v-model="pagination.currentPage"
            :total-rows="pagination.totalRows"
            :rows-per-page="pagination.perPage"
          />
        </div>

      </div>

    </div>

  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
const ListSearch = resolve => require(['@/components/ListSearch'], resolve)
const PaginationComponent = resolve => require(['@/components/Pagination'], resolve)
const FilteringToggleButton = resolve => require(['@/components/filters/FilteringToggleButton'], resolve)
const JobFilters = resolve => require(['@/components/filters/Filters'], resolve)

export default {
  props: {
    customerID: {},
    vehicleID: {},
    hideJobID: {
      type: Array,
      default: () => [],
    },
    showCustomerColumn: {
      type: Boolean,
      default: true,
    },
    buttonNew: {
      type: Boolean,
      default: true,
    },
  },
  components: {
    FilteringToggleButton,
    ListSearch,
    JobFilters,
    PaginationComponent,
  },
  data() {
    return {
      jobs: [],
      searchInput: '',
      query: {
        search: '',
      },
      filtering: {
        active: false,
        hasChanges: false,
      },
      filters: {
        status: {
          key: 'status',
          name: 'Status',
          active: true,
          options: [
            {name: 'Completed', value: 'completed', class: 'btn-green'},
            {name: 'Ongoing', value: 'ongoing', class: 'btn-orange'},
          ],
          value: null,
        },
        createdAt: {
          key: 'created_at',
          name: 'Created at date',
          active: true,
          flatpickr: {
            mode: 'range',
            static: false,
          },
          value: null,
        },
      },
      pagination: {
        currentPage: 1,
        totalRows: 0,
        perPage: 0,
      }
    }
  },
  watch: {
    'pagination.currentPage'() {
      this.getJobs()
    },
    searchInput(i) {
      if (i === '' && this.query.search !== '') {
        this.getJobs()
      }
    },
    customerID() {
      this.getJobs()
    },
    vehicleID() {
      this.getJobs()
    },
  },
  computed: {
    displayJobs() {
      if (!this.jobs || !this.jobs.length) {
        return []
      }
      return !this.hideJobID.length ? this.jobs : this.jobs.filter(e => !this.hideJobID.includes(e.id))
    },
    hasJobs() {
      return Object.entries(this.jobs).length > 0
    },
    ...mapGetters({
      config: 'config/config',
      hasPermission: 'user/hasPermission',
    })
  },
  mounted() {
    this.getJobs()
    this.$EventBus.$on('vehicle.updated', this.getJobs)
  },
  destroyed() {
    this.$EventBus.$off('vehicle.updated')
  },
  methods: {
    deleteJobs(jobID) {
      this.$swal.fire({
        title: 'Delete job',
        text: 'Are you sure?',
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete'
      })
        .then((result) => {
          if (result.value) {
            // send delete
            axios.delete('jobs/' + jobID)
              .then(() => {
                this.$swal.fire('Delete successful', '', 'success')
                this.getJobs()
              })
          }
        })
    },
    getJobs() {
      let filters = this.$getFilterValues(this.filters)
      if (filters.created_at) {
        let createdAt = this.$getRangeDates(filters.created_at, this.config)
        filters.dateFrom = createdAt.from
        filters.dateTo = createdAt.to
      }

      axios.get('jobs', {params:{
        page: this.pagination.currentPage,
        search: this.searchInput,
        customerID: this.customerID,
        vehicleID: this.vehicleID,
        filters,
      }})
        .then(response => {
          if (!response.data) {
            return
          }
          const res = response.data
          this.pagination.perPage = res.per_page
          this.pagination.totalRows = res.total
          this.jobs = Object.assign([], res.data)

          this.query.search = this.searchInput
        })
    },
    goToCreateJob() {
      this._checkLimitsCanCreateJob()
        .then(() => {
          let customerID = this.customerID
          this.$router.push({ name: 'job.create', params: customerID ? { customerID } : {} })
        })
        .catch((errorMessage) => {
          this.$notify({
            type: 'error',
            title: 'Cannot create job',
            text: errorMessage
          })
        })
    },
    _checkLimitsCanCreateJob() {
      return new Promise((resolve, reject) => {
        axios.get('job/check-limits-can-create')
          .then(response => {
            if (!response.data) {
              reject('invalid response')
            }
            if (response.data.can_create) {
              resolve()
            } else {
              reject(response.data.error_message)
            }
          })
          .catch(error => {
            reject(error.statusText)
          })
      })
    }
  }
}
</script>
